import React, { useState } from "react";
import { toast } from "react-toastify";
import * as CampaignsAPI from "../../services/api/Campaigns";
import DatePicker from "react-datetime";
import moment from "moment";
import { Tooltip } from "reactstrap";
import { BATCH_SIZE } from "./constants";
import {
  DEFAULT_SEND_SCHEDULE,
  SendSchedule,
  SendTimeInput,
  describeSendSchedule,
  sendScheduleError,
  sendScheduleParams,
} from "./SendTimeInput";

const Spinner: React.FC = () => (
  <span
    className="spinner-border spinner-border-sm mr-2"
    role="status"
    aria-hidden="true"
  />
);

// Sequence display names
const SEQUENCE_NAMES: Record<string, string> = {
  sequence01: "Sequence 01 - Q&A, AMA, Templates, Dashboard, Upgrade",
  sequence02: "Sequence 02 - Strategic Offsite & Meeting Prep",
  sequence03: "Sequence 03 - Surveys, Trivia, & AI Templates",
  sequence04: "Sequence 04 - AI Visibility, Alignment, Q&A, Themes",
};

// Cadence options in days
const CADENCE_OPTIONS = [
  { value: 7, label: "7 days" },
  { value: 14, label: "14 days" },
  { value: 21, label: "21 days" },
  { value: 28, label: "28 days" },
];

// DM descriptions for preview
const DM_DESCRIPTIONS: Record<string, string[]> = {
  sequence01: [
    "DM1: Q&A - Live Q&A for all-hands",
    "DM2: AMA - Ask Me Anything template",
    "DM3: Templates - Internal Comms templates",
    "DM4: Dashboard - Web dashboard overview",
    "DM5: Upgrade - See plans & start trial",
  ],
  sequence02: [
    "DM1: Automation & Google Sheets",
    "DM2: Reminders & Time Savings",
    "DM3: Offsite & Meeting Prep Templates",
    "DM4: Q&A / Trivia & Quizzes",
    "DM5: Polly Pro & Free Trial",
  ],
  sequence03: [
    "DM1: Pulse Surveys - Get the 'silent majority' talking again",
    "DM2: Response Rates - When feedback shows up where people already work",
    "DM3: Trivia - Weekly trivia games",
    "DM4: Quizzes - Quarterly surveys",
    "DM5: AI Templates - Measure AI adoption or understand team AI training needs",
  ],
  sequence04: [
    "DM1: AI Visibility - Explore AI Templates & Product Tour",
    "DM2: Alignment - Create Polly for quick alignment",
    "DM3: Town Hall Q&A - Create Q&A & Watch demo",
    "DM4: Themes - Watch Quick Demo for live results in Slides/PPT/Notion",
    "DM5: Upgrade/Trial - Upgrade (Creators) or Start Free Trial (Non-Creators)",
  ],
};

const SEQUENCE_CONFIG_DM_COUNT = 5;

export const SequenceTab: React.FC = () => {
  const [sequenceAction, setSequenceAction] = useState<"schedule" | "remove">(
    "schedule",
  );
  const [sequenceId, setSequenceId] = useState("sequence01");
  const [arm, setArm] = useState<"A" | "B">("A");
  const [startDate, setStartDate] = useState<moment.Moment>(
    moment().add(1, "day").startOf("day"),
  );
  const [cadenceDays, setCadenceDays] = useState(14);
  const [sendSchedule, setSendSchedule] = useState<SendSchedule>(
    DEFAULT_SEND_SCHEDULE,
  );
  const [userIdsText, setUserIdsText] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmationDetails, setConfirmationDetails] = useState<{
    action: "schedule" | "remove";
    userCount: number;
    sequenceId: string;
    arm: "A" | "B";
    startDate?: string;
    cadenceDays?: number;
    preview?: { dmNumber: number; date: string; description: string }[];
  } | null>(null);
  const [csvConfirmation, setCsvConfirmation] = useState<{
    fileName: string;
    userCount: number;
    userIds: string[];
  } | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [csvTooltipOpen, setCsvTooltipOpen] = useState(false);

  const isValidDate = (current: moment.Moment) => {
    const today = moment().startOf("day");
    return current.isSameOrAfter(today);
  };

  const getUserIds = () => {
    return userIdsText.split(/[\s,]+/).filter(Boolean);
  };

  const calculatePreview = () => {
    const descriptions = DM_DESCRIPTIONS[sequenceId] || [];
    return descriptions.map((desc, index) => ({
      dmNumber: index + 1,
      date: moment(startDate)
        .add(index * cadenceDays, "days")
        .format("MMM D, YYYY"),
      description: desc,
    }));
  };

  const handleScheduleRequest = () => {
    const userIds = getUserIds();
    if (userIds.length === 0) {
      toast.error("Please enter at least one user ID.");
      return;
    }
    if (sendScheduleError(sendSchedule)) {
      toast.error(sendScheduleError(sendSchedule));
      return;
    }

    setConfirmationDetails({
      action: "schedule",
      userCount: userIds.length,
      sequenceId,
      arm,
      startDate: startDate.format("LL"),
      cadenceDays,
      preview: calculatePreview(),
    });
  };

  const handleRemoveRequest = () => {
    const userIds = getUserIds();
    if (userIds.length === 0) {
      toast.error("Please enter at least one user ID.");
      return;
    }

    setConfirmationDetails({
      action: "remove",
      userCount: userIds.length,
      sequenceId,
      arm,
    });
  };

  const handleScheduleSequence = async () => {
    setConfirmationDetails(null);
    setSuccessMessage(null);
    const userIds = getUserIds();

    // Split userIds into batches to avoid payload too large errors
    const batches: string[][] = [];
    for (let i = 0; i < userIds.length; i += BATCH_SIZE) {
      batches.push(userIds.slice(i, i + BATCH_SIZE));
    }

    setLoading(true);
    try {
      // Fire all batch requests in parallel without waiting (fire-and-forget)
      batches.forEach((batch) =>
        CampaignsAPI.scheduleSequence({
          userIds: batch,
          sequenceId,
          arm,
          startDate: startDate.format("YYYY-MM-DD"),
          cadenceDays,
          ...sendScheduleParams(sendSchedule),
        }),
      );

      const batchLabel = batches.length > 1 ? "batches" : "batch";
      setSuccessMessage(
        `✅ Request submitted for ${userIds.length} users (${batches.length} ${batchLabel}).\n\nResults will be posted to #project-dm-campaign channel.`,
      );
    } catch (error: any) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSequence = async () => {
    setConfirmationDetails(null);
    setSuccessMessage(null);
    const userIds = getUserIds();

    const batches: string[][] = [];
    for (let i = 0; i < userIds.length; i += BATCH_SIZE) {
      batches.push(userIds.slice(i, i + BATCH_SIZE));
    }

    setLoading(true);
    try {
      batches.forEach((batch) =>
        CampaignsAPI.removeSequence({
          userIds: batch,
          sequenceId,
          arm,
        }),
      );

      const batchLabel = batches.length > 1 ? "batches" : "batch";
      setSuccessMessage(
        `Request to remove ${SEQUENCE_NAMES[sequenceId]} (Arm ${arm}) submitted for ${userIds.length} users (${batches.length} ${batchLabel}).\n\nResults will be posted to #project-dm-campaign channel.`,
      );
    } catch (error: any) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split(/\r?\n/).filter(Boolean);
      if (lines.length <= 1) {
        toast.error("CSV file must have a header and at least one user ID.");
        return;
      }
      const userIds = lines
        .slice(1)
        .map((line) => line.split(",")[0].trim())
        .filter(Boolean);

      if (userIds.length === 0) {
        toast.error("No user IDs found in the CSV file.");
        return;
      }

      setCsvConfirmation({
        fileName: file.name,
        userCount: userIds.length,
        userIds,
      });
    };
    reader.readAsText(file);
    event.target.value = "";
  };

  const handleConfirmCSV = () => {
    if (csvConfirmation) {
      setUserIdsText(csvConfirmation.userIds.join("\n"));
      setCsvConfirmation(null);
    }
  };

  return (
    <>
      {/* Action Toggle */}
      <div className="d-flex mb-4" role="group">
        <button
          type="button"
          className={`btn w-100 mr-2 ${
            sequenceAction === "schedule"
              ? "btn-success"
              : "btn-outline-success"
          }`}
          onClick={() => setSequenceAction("schedule")}
        >
          Schedule Sequence
        </button>
        <button
          type="button"
          className={`btn w-100 ${
            sequenceAction === "remove"
              ? "btn-warning text-white"
              : "btn-outline-warning text-dark"
          }`}
          onClick={() => setSequenceAction("remove")}
        >
          Remove Sequence
        </button>
      </div>

      {/* Sequence Selection */}
      <div className="form-group mb-4">
        <label className="font-weight-bold mb-2">Sequence</label>
        <select
          className="form-control"
          value={sequenceId}
          onChange={(e) => setSequenceId(e.target.value)}
        >
          {Object.entries(SEQUENCE_NAMES).map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
      </div>

      {/* Arm Selection */}
      <div className="form-group mb-4">
        <label className="font-weight-bold mb-2">Arm</label>
        <div className="d-flex">
          <div className="custom-control custom-radio mr-4">
            <input
              type="radio"
              id="armA"
              name="arm"
              className="custom-control-input"
              checked={arm === "A"}
              onChange={() => setArm("A")}
            />
            <label className="custom-control-label" htmlFor="armA">
              <strong>Arm A</strong> - Creators
            </label>
          </div>
          <div className="custom-control custom-radio">
            <input
              type="radio"
              id="armB"
              name="arm"
              className="custom-control-input"
              checked={arm === "B"}
              onChange={() => setArm("B")}
            />
            <label className="custom-control-label" htmlFor="armB">
              <strong>Arm B</strong> - Non-Creators
            </label>
          </div>
        </div>
      </div>

      {/* Start Date & Cadence (schedule only) */}
      {sequenceAction === "schedule" && (
        <>
          <div className="row">
            <div className="col-md-6">
              <div className="form-group mb-4">
                <label className="font-weight-bold mb-2">
                  Start Date (DM1)
                </label>
                <DatePicker
                  value={startDate}
                  onChange={(date) => setStartDate(date as moment.Moment)}
                  timeFormat={false}
                  isValidDate={isValidDate}
                  inputProps={{ className: "form-control" }}
                />
                <small className="form-text text-muted">
                  First DM will be sent on this date at the send time below, in
                  the user's timezone.
                </small>
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group mb-4">
                <label className="font-weight-bold mb-2">Cadence</label>
                <select
                  className="form-control"
                  value={cadenceDays}
                  onChange={(e) => setCadenceDays(Number(e.target.value))}
                >
                  {CADENCE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <small className="form-text text-muted">
                  Days between each DM in the sequence.
                </small>
              </div>
            </div>
          </div>

          <div className="form-group mb-4">
            <SendTimeInput
              id="legacySequenceSendTime"
              value={sendSchedule}
              onChange={setSendSchedule}
            />
            <small className="form-text text-muted">
              In the user&apos;s timezone. With a window, each user keeps their
              slot for every DM in the sequence.
            </small>
          </div>

          {/* Schedule Preview */}
          <div className="form-group mb-4">
            <label className="font-weight-bold mb-2">Schedule Preview</label>
            <div className="card bg-light">
              <div className="card-body py-2">
                {calculatePreview().map((item) => (
                  <div
                    key={item.dmNumber}
                    className="d-flex justify-content-between py-1"
                  >
                    <span className="text-muted">{item.description}</span>
                    <span className="font-weight-bold">{item.date}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Remove info */}
      {sequenceAction === "remove" && (
        <div className="alert alert-warning mb-4">
          <strong>Remove Sequence</strong> — This will remove all{" "}
          {SEQUENCE_CONFIG_DM_COUNT} DMs in the selected sequence and arm from
          the specified users. Already-sent DMs will be skipped and left intact.
        </div>
      )}

      {/* User IDs */}
      <div className="form-group mb-4">
        <label className="font-weight-bold mb-2">User IDs</label>
        <div className="d-flex align-items-center mb-2">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => fileInputRef.current?.click()}
          >
            Upload CSV
          </button>
          <i
            className="fa fa-info-circle text-muted ml-2"
            id="seq-csv-upload-info"
            style={{ cursor: "pointer" }}
          />
          <Tooltip
            placement="right"
            isOpen={csvTooltipOpen}
            target="seq-csv-upload-info"
            toggle={() => setCsvTooltipOpen(!csvTooltipOpen)}
          >
            Upload a CSV file with user IDs in the first column.
          </Tooltip>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".csv"
            style={{ display: "none" }}
          />
        </div>
        <textarea
          className="form-control"
          rows={5}
          value={userIdsText}
          onChange={(e) => setUserIdsText(e.target.value)}
          placeholder="Enter user mongo IDs separated by commas, spaces, or new lines."
        />
      </div>

      <hr className="my-4" />

      {/* Submit Button */}
      <div className="d-flex justify-content-end">
        {sequenceAction === "schedule" ? (
          <button
            className="btn btn-success"
            onClick={handleScheduleRequest}
            disabled={loading}
          >
            {loading && <Spinner />}
            Schedule Sequence
          </button>
        ) : (
          <button
            className="btn btn-warning text-white"
            onClick={handleRemoveRequest}
            disabled={loading}
          >
            {loading && <Spinner />}
            Remove Sequence
          </button>
        )}
      </div>

      {/* Success Message */}
      {successMessage && (
        <div
          className="alert alert-success alert-dismissible fade show mt-4"
          role="alert"
          style={{ whiteSpace: "pre-line" }}
        >
          {successMessage}
          <button
            type="button"
            className="close"
            onClick={() => setSuccessMessage(null)}
          >
            <span>&times;</span>
          </button>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmationDetails && (
        <div
          className="modal"
          tabIndex={-1}
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {confirmationDetails.action === "schedule"
                    ? "Confirm Sequence Schedule"
                    : "Confirm Sequence Removal"}
                </h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setConfirmationDetails(null)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p>
                  {confirmationDetails.action === "schedule"
                    ? "Please confirm the sequence scheduling details:"
                    : "Please confirm the sequence removal details:"}
                </p>
                <ul className="list-group mb-3">
                  <li className="list-group-item d-flex justify-content-between">
                    <span>Action</span>
                    <span
                      className={`badge badge-pill ${
                        confirmationDetails.action === "schedule"
                          ? "badge-success"
                          : "badge-warning"
                      }`}
                    >
                      {confirmationDetails.action === "schedule"
                        ? "Schedule"
                        : "Remove"}
                    </span>
                  </li>
                  <li className="list-group-item d-flex justify-content-between">
                    <span>Sequence</span>
                    <strong>
                      {SEQUENCE_NAMES[confirmationDetails.sequenceId]}
                    </strong>
                  </li>
                  <li className="list-group-item d-flex justify-content-between">
                    <span>Arm</span>
                    <strong>
                      {confirmationDetails.arm} (
                      {confirmationDetails.arm === "A"
                        ? "Creators"
                        : "Non-Creators"}
                      )
                    </strong>
                  </li>
                  <li className="list-group-item d-flex justify-content-between">
                    <span>Users</span>
                    <strong>{confirmationDetails.userCount}</strong>
                  </li>
                  {confirmationDetails.action === "schedule" && (
                    <li className="list-group-item d-flex justify-content-between">
                      <span>Send Time</span>
                      <strong>
                        {describeSendSchedule(sendSchedule)} in each user&apos;s
                        timezone
                      </strong>
                    </li>
                  )}
                  {confirmationDetails.cadenceDays && (
                    <li className="list-group-item d-flex justify-content-between">
                      <span>Cadence</span>
                      <strong>
                        Every {confirmationDetails.cadenceDays} days
                      </strong>
                    </li>
                  )}
                </ul>

                {confirmationDetails.preview && (
                  <>
                    <h6>Schedule:</h6>
                    <table className="table table-sm table-bordered">
                      <thead className="thead-light">
                        <tr>
                          <th>DM</th>
                          <th>Send Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {confirmationDetails.preview.map((item) => (
                          <tr key={item.dmNumber}>
                            <td>{item.description}</td>
                            <td className="font-weight-bold">{item.date}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {confirmationDetails.action === "remove" && (
                  <div className="alert alert-warning mb-0">
                    This will remove all {SEQUENCE_CONFIG_DM_COUNT} DMs in this
                    sequence from the specified users. Already-sent Slack
                    messages will NOT be deleted.
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setConfirmationDetails(null)}
                >
                  Cancel
                </button>
                {confirmationDetails.action === "schedule" ? (
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleScheduleSequence}
                  >
                    Confirm & Schedule
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-warning text-white"
                    onClick={handleRemoveSequence}
                  >
                    Confirm & Remove
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV Confirmation Modal */}
      {csvConfirmation && (
        <div
          className="modal"
          tabIndex={-1}
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm CSV Upload</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setCsvConfirmation(null)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p>File: {csvConfirmation.fileName}</p>
                <p>Users found: {csvConfirmation.userCount}</p>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setCsvConfirmation(null)}
                >
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleConfirmCSV}>
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
